// FolderTree (DESIGN §3.4.7): Ordnerbaum (W2 links 260, AI9-Picker). Zeile 36, Einrückung 16 je
// Ebene, Chevron 16, `FolderSimple` bzw. `FolderOpen`, Name `label`, Zähler rechts `small`
// `fg-tertiary`, Schloss bei eigenen Rechten; aktiv `selection`. Drop-Ziel: `accent-soft` + Ring.
// Tastatur nach WAI-ARIA Tree (↑/↓, ←/→, Home/End, Typeahead, Enter wählt). Mobil: Select mit
// eingerückten Optionen.
import {
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import {
  CaretRightIcon,
  FolderOpenIcon,
  FolderSimpleIcon,
  LockSimpleIcon,
} from '@phosphor-icons/react';
import { allRows, treeKey, visibleRows } from './_lib/tree';
import { useControllable } from './_lib/useControllable';
import { Select } from './Select';
import { cn } from '../basis/kern';

export interface FolderNode {
  id: string;
  name: string;
  /** Anzahl Dateien (rechts). */
  count?: number;
  /** Eigene Rechte statt geerbter: Schloss-Icon. */
  restricted?: boolean;
  /** Eigenes Icon statt Ordner (z. B. Papierkorb). */
  icon?: ReactNode;
  children?: FolderNode[];
}

export interface FolderTreeProps {
  nodes: FolderNode[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  expandedIds?: string[];
  defaultExpandedIds?: string[];
  onExpandedChange?: (ids: string[]) => void;
  /** Dateien auf einen Ordner gezogen; ohne Handler ist der Baum kein Drop-Ziel. */
  onDropOnFolder?: (id: string, event: DragEvent<HTMLElement>) => void;
  /** Unter `md` als Select (Standard an). */
  mobileSelect?: boolean;
  'aria-label'?: string;
  className?: string;
}

const EMPTY: string[] = [];

export function FolderTree({
  nodes,
  selectedId,
  onSelect,
  expandedIds,
  defaultExpandedIds = EMPTY,
  onExpandedChange,
  onDropOnFolder,
  mobileSelect = true,
  'aria-label': ariaLabel = 'Folders',
  className,
}: FolderTreeProps) {
  const [expandedList, setExpandedList] = useControllable(
    expandedIds,
    defaultExpandedIds,
    onExpandedChange,
  );
  const expanded = useMemo(() => new Set(expandedList), [expandedList]);
  const rows = useMemo(() => visibleRows(nodes, expanded), [nodes, expanded]);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [dropId, setDropId] = useState<string | null>(null);
  const treeRef = useRef<HTMLUListElement>(null);
  // Genau eine Zeile ist per Tab erreichbar: die fokussierte, sonst die gewählte, sonst die erste.
  const tabId =
    [focusedId, selectedId].find((id) => id && rows.some((r) => r.node.id === id)) ??
    rows[0]?.node.id ??
    null;

  const setOpen = (id: string, open: boolean) =>
    setExpandedList(
      open ? [...expandedList.filter((x) => x !== id), id] : expandedList.filter((x) => x !== id),
    );

  const focusRow = (id: string) => {
    setFocusedId(id);
    treeRef.current?.querySelector<HTMLElement>(`[data-tree-id="${CSS.escape(id)}"]`)?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (!tabId) return;
    const action = treeKey(rows, tabId, event.key);
    if (!action) return;
    event.preventDefault();
    if (action.type === 'focus') focusRow(action.id);
    else if (action.type === 'expand') setOpen(action.id, true);
    else if (action.type === 'collapse') setOpen(action.id, false);
    else onSelect(action.id);
  };

  const dropHandlers = (id: string) =>
    onDropOnFolder
      ? {
          onDragOver: (event: DragEvent<HTMLElement>) => {
            event.preventDefault();
            if (dropId !== id) setDropId(id);
          },
          onDragLeave: () => setDropId((current) => (current === id ? null : current)),
          onDrop: (event: DragEvent<HTMLElement>) => {
            event.preventDefault();
            setDropId(null);
            onDropOnFolder(id, event);
          },
        }
      : {};

  const tree = (
    <ul
      ref={treeRef}
      role="tree"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className="flex flex-col gap-0.5"
    >
      {rows.map(({ node, level, hasChildren, expanded: isOpen }) => {
        const selected = node.id === selectedId;
        const icon = node.icon ?? (isOpen ? <FolderOpenIcon /> : <FolderSimpleIcon />);
        return (
          // Tastatur führt der Baum (onKeyDown am `tree`, Roving-Fokus nach WAI-ARIA).
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <li
            key={node.id}
            role="treeitem"
            aria-level={level}
            aria-selected={selected}
            aria-expanded={hasChildren ? isOpen : undefined}
            data-tree-id={node.id}
            data-drop={dropId === node.id || undefined}
            tabIndex={node.id === tabId ? 0 : -1}
            onFocus={(event) => {
              if (event.target === event.currentTarget) setFocusedId(node.id);
            }}
            onClick={() => onSelect(node.id)}
            {...dropHandlers(node.id)}
            style={{ paddingLeft: `calc(var(--spacing) * ${2 + (level - 1) * 4})` }}
            className={cn(
              'flex h-9 cursor-default items-center gap-2 rounded-sm pr-2 text-label text-fg-secondary select-none',
              'transition-colors duration-(--bar-dur-fast) ease-standard hover:bg-tile hover:text-fg',
              'focus-visible:outline-offset-[-2px]',
              selected && 'bg-selection text-fg hover:bg-selection-hover',
              'data-[drop=true]:bg-accent-soft data-[drop=true]:ring-1 data-[drop=true]:ring-accent data-[drop=true]:ring-inset',
            )}
          >
            <span
              aria-hidden="true"
              onClick={(event) => {
                if (!hasChildren) return;
                event.stopPropagation();
                setOpen(node.id, !isOpen);
              }}
              className={cn(
                'inline-flex size-4 shrink-0 items-center justify-center text-fg-tertiary transition-transform duration-(--bar-dur-fast) ease-standard',
                !hasChildren && 'invisible',
                isOpen && 'rotate-90',
              )}
            >
              <CaretRightIcon size={12} weight="bold" />
            </span>
            <span
              aria-hidden="true"
              className={cn(
                'inline-flex shrink-0 [&>svg]:size-4',
                selected ? 'text-fg' : 'text-fg-secondary',
              )}
            >
              {icon}
            </span>
            <span className="min-w-0 flex-1 truncate">{node.name}</span>
            {node.restricted ? (
              <LockSimpleIcon
                size={14}
                aria-label="Own permissions"
                className="shrink-0 text-fg-tertiary"
              />
            ) : null}
            {node.count !== undefined ? (
              <span className="shrink-0 text-small text-fg-secondary tabular-nums">
                {node.count}
              </span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );

  if (!mobileSelect) return <div className={className}>{tree}</div>;
  return (
    <div className={className}>
      <div className="max-md:hidden">{tree}</div>
      <div className="md:hidden">
        <Select
          variant="pill"
          icon={<FolderSimpleIcon />}
          className="w-full"
          aria-label={ariaLabel}
          value={selectedId ?? undefined}
          onValueChange={onSelect}
          options={allRows(nodes).map(({ node, level }) => ({
            value: node.id,
            label: node.name,
            leading: (
              <span
                className="inline-flex items-center"
                style={{ paddingLeft: `calc(var(--spacing) * ${(level - 1) * 4})` }}
              >
                {node.icon ?? <FolderSimpleIcon />}
              </span>
            ),
          }))}
        />
      </div>
    </div>
  );
}
